import React, { useEffect, useRef, useState, useCallback } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';
import './MapComponent.css';
import { RouteInfoPanel } from '../RouteInfo';
import { useZoneEvents } from '../../hooks/useZoneEvents';
import { useCoordinateTooltip } from '../../hooks/useCoordinateTooltip';
import { API_BASE } from '../../config';

const MapComponent = ({ 
  sopRoutes = [], 
  vesselRuns = [], 
  selectedSopRoute = null,
  selectedRun = null,
  onRunSelect,
  mapStyle = 'standard',
  isPlaybackActive: externalIsPlaybackActive = false,
  onPlaybackActiveChange,
  geofences = null,
  drawMode = { active: false, zoneType: 'segment' },
  onGeofenceCreated,
  playbackIndex: externalPlaybackIndex = 0,
  isPlaying: externalIsPlaying = false,
}) => {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [selectedDatapoint, setSelectedDatapoint] = useState(null);
  const [currentZoomLevel, setCurrentZoomLevel] = useState(11);
  const [isPlaybackActive, setIsPlaybackActive] = useState(externalIsPlaybackActive);
  
  const layersRef = useRef({
    sopRoutes: L.layerGroup(),
    vesselRuns: L.layerGroup(),
    selected: L.layerGroup(),
    datapoints: L.layerGroup(),
    playback: L.layerGroup(),
    geofences: L.layerGroup(),
    zoneEvents: L.layerGroup()
  });
  
  const hoveredLayerRef = useRef(null);
  const vesselRunLayersRef = useRef(new Map());
  const datapointMarkersRef = useRef(new Map());
  const hasInitialFitRef = useRef(false);
  const currentRouteDataRef = useRef(null);
  const detailedPointsDataRef = useRef(null);
  
  const vesselMarkerRef = useRef(null);
  const completedPathRef = useRef(null);
  const remainingPathRef = useRef(null);
  const drawControlRef = useRef(null);
  const drawnItemsRef = useRef(L.featureGroup());
  const drawModeRef = useRef(drawMode);
  const onGeofenceCreatedRef = useRef(onGeofenceCreated);

  // Coordinate tooltip — only activates once the map instance is ready
  useCoordinateTooltip(mapInstance, mapReady);

  useEffect(() => {
    drawModeRef.current = drawMode;
  }, [drawMode]);

  useEffect(() => {
    onGeofenceCreatedRef.current = onGeofenceCreated;
  }, [onGeofenceCreated]);

  const runId = selectedRun?.properties?.run_id || null;
  const { zoneEvents, loading: zoneEventsLoading } = useZoneEvents(runId);

  // Sync with external playback state
  useEffect(() => {
    if (externalIsPlaybackActive !== isPlaybackActive) {
      setIsPlaybackActive(externalIsPlaybackActive);
    }
  }, [externalIsPlaybackActive, isPlaybackActive]);

  const extractCoordinates = (geometry = {}) => {
    if (geometry.type === 'LineString') return geometry.coordinates;
    if (geometry.type === 'MultiLineString') return geometry.coordinates.flat();
    return [];
  };

  const downsampleCoordinates = (coordinates, zoomLevel) => {
    if (!Array.isArray(coordinates) || coordinates.length === 0) return [];
    if (zoomLevel >= 14) return coordinates;
    if (zoomLevel >= 12) return coordinates.filter((_, index) => index % 3 === 0);
    return coordinates.filter((_, index) => index % 5 === 0);
  };

  const createDatapointIcon = (isSelected = false) => {
    const size = isSelected ? 10 : 6;
    const color = isSelected ? '#00ff00' : '#ffffff';
    const borderColor = isSelected ? '#00cc00' : '#333333';
    const borderWidth = isSelected ? 2 : 1;
    
    return L.divIcon({
      className: 'datapoint-marker',
      html: `<div class="datapoint-marker-inner" style="
        width: ${size}px; height: ${size}px;
        background-color: ${color}; border: ${borderWidth}px solid ${borderColor};
        border-radius: 50%;
        box-shadow: ${isSelected ? '0 0 8px rgba(0, 255, 0, 0.8)' : '0 1px 3px rgba(0,0,0,0.4)'};
      "></div>`,
      iconSize: [20, 20],
      iconAnchor: [10, 10]
    });
  };

  const createZoneEventIcon = (eventType, zoneType) => {
    const zoneColors = {
      restricted: '#ef4444', start_zone: '#10b981', end_zone: '#3b82f6',
      waypoint: '#f59e0b', general: '#6b7280'
    };
    const color = zoneColors[zoneType] || zoneColors.general;
    const icon = eventType === 'enter' ? '▶' : '✕';
    const size = 24;
    
    return L.divIcon({
      className: 'zone-event-marker',
      html: `<div class="zone-event-marker-inner" style="
        width: ${size}px; height: ${size}px; background-color: ${color};
        color: white; border: 2px solid white; border-radius: 50%;
        display: flex; align-items: center; justify-content: center;
        font-size: 14px; font-weight: bold;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
      ">${icon}</div>`,
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
      popupAnchor: [0, -size / 2]
    });
  };

  const createVesselIcon = (heading = 0) => {
    return L.divIcon({
      className: 'vessel-marker',
      html: `<div class="vessel-marker-icon" style="transform: rotate(${heading}deg);">
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
          <path d="M16 4 L24 28 L16 24 L8 28 Z" fill="#4299e1" stroke="white" stroke-width="2"/>
        </svg>
      </div>`,
      iconSize: [32, 32],
      iconAnchor: [16, 16]
    });
  };

  const fetchDetailedPoints = async (runId) => {
    try {
      const response = await fetch(`${API_BASE}/api/runs/${runId}`);
      if (!response.ok) return null;
      const data = await response.json();
      return data.points?.features || null;
    } catch (err) {
      console.error('[MapComponent] Error fetching detailed points:', err);
      return null;
    }
  };

  const renderZoneEvents = useCallback((events, currentPlaybackIndex = null) => {
    if (!mapInstance.current || !events) return;
    layersRef.current.zoneEvents.clearLayers();
    if (!events.features || events.features.length === 0) return;

    let visibleEvents = events.features;
    
    if (isPlaybackActive && currentPlaybackIndex !== null && selectedRun) {
      if (detailedPointsDataRef.current && currentPlaybackIndex < detailedPointsDataRef.current.length) {
        const currentTimestamp = new Date(detailedPointsDataRef.current[currentPlaybackIndex].properties.time);
        visibleEvents = events.features.filter(event => {
          const eventTime = new Date(event.properties.event_timestamp);
          return eventTime <= currentTimestamp;
        });
      }
    }

    visibleEvents.forEach((event) => {
      const { latitude, longitude, event_type, zone_name, zone_type, event_timestamp } = event.properties;
      if (!latitude || !longitude) return;

      const marker = L.marker([latitude, longitude], {
        icon: createZoneEventIcon(event_type, zone_type),
        zIndexOffset: 1500
      });

      const eventDate = new Date(event_timestamp);
      const timeString = eventDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

      marker.bindPopup(`
        <div style="font-family: Arial, sans-serif; min-width: 180px;">
          <strong style="color: ${zone_type === 'restricted' ? '#ef4444' : '#4299e1'};">
            ${event_type.toUpperCase()}
          </strong><br>
          <strong>Zone:</strong> ${zone_name}<br>
          <strong>Type:</strong> ${zone_type.replace('_', ' ')}<br>
          <strong>Time:</strong> ${timeString}<br>
        </div>
      `);
      marker.bindTooltip(`${event_type}: ${zone_name}`, { direction: 'top', offset: [0, -12], permanent: false });
      layersRef.current.zoneEvents.addLayer(marker);
    });
  }, [isPlaybackActive, selectedRun]);

  const renderDatapointMarkers = async (routeData, routeType = 'run') => {
    if (!mapInstance.current || !routeData || !routeData.geometry) return;
    if (isPlaybackActive) return;

    currentRouteDataRef.current = { routeData, routeType };
    layersRef.current.datapoints.clearLayers();
    datapointMarkersRef.current.clear();

    let detailedPoints = null;
    if (routeType === 'run' && routeData.properties?.run_id) {
      detailedPoints = await fetchDetailedPoints(routeData.properties.run_id);
      detailedPointsDataRef.current = detailedPoints;
    }

    const allCoordinates = extractCoordinates(routeData.geometry);
    if (allCoordinates.length === 0) return;

    const coordinates = downsampleCoordinates(allCoordinates, currentZoomLevel);

    coordinates.forEach((coord, displayIndex) => {
      const [lng, lat] = coord;
      if (!lat || !lng || isNaN(lat) || isNaN(lng)) return;

      const actualIndex = allCoordinates.findIndex(c => c[0] === lng && c[1] === lat);
      let pointData = null;
      if (detailedPoints && actualIndex < detailedPoints.length) {
        pointData = detailedPoints[actualIndex]?.properties;
      }
      
      const isSelected = selectedDatapoint?.index === actualIndex;
      const marker = L.marker([lat, lng], {
        icon: createDatapointIcon(isSelected),
        zIndexOffset: isSelected ? 1000 : 0,
        interactive: true, bubblingMouseEvents: false
      });

      const handleClick = (e) => {
        L.DomEvent.stopPropagation(e);
        L.DomEvent.preventDefault(e);
        setSelectedDatapoint({
          index: actualIndex,
          coordinates: { latitude: lat, longitude: lng },
          timestamp: pointData?.time || null,
          speed: pointData?.sog || null,
          cog: pointData?.cog || null,
          heading: pointData?.hdg || null,
          depth: pointData?.dpt || null,
          routeId: routeData.properties?.run_id || routeData.properties?.route_id,
          routeType, totalPoints: allCoordinates.length
        });
      };

      marker.on('mousedown', handleClick);
      marker.on('click', handleClick);

      const tooltipText = pointData 
        ? `Point ${actualIndex + 1}: ${pointData.sog ? pointData.sog.toFixed(1) + ' kts' : 'N/A'}`
        : `Point ${actualIndex + 1} of ${allCoordinates.length}`;
      marker.bindTooltip(tooltipText, { direction: 'top', offset: [0, -5], permanent: false });
      layersRef.current.datapoints.addLayer(marker);
      datapointMarkersRef.current.set(actualIndex, marker);
    });
  };

  // ── Core playback rendering driven by externalPlaybackIndex ──
  const updatePlaybackVisuals = useCallback(async (index) => {
    if (!mapInstance.current || !selectedRun) return;

    const coordinates = extractCoordinates(selectedRun.geometry);
    if (!coordinates || coordinates.length === 0 || index >= coordinates.length) return;

    const [lng, lat] = coordinates[index];
    let heading = 0;

    if (!detailedPointsDataRef.current && selectedRun.properties?.run_id) {
      detailedPointsDataRef.current = await fetchDetailedPoints(selectedRun.properties.run_id);
    }

    if (detailedPointsDataRef.current && index < detailedPointsDataRef.current.length) {
      const detailedPoint = detailedPointsDataRef.current[index]?.properties;
      heading = detailedPoint?.hdg ?? detailedPoint?.cog ?? 0;
    }

    // Vessel marker
    if (vesselMarkerRef.current) {
      vesselMarkerRef.current.setLatLng([lat, lng]);
      vesselMarkerRef.current.setIcon(createVesselIcon(heading));
    } else {
      vesselMarkerRef.current = L.marker([lat, lng], { icon: createVesselIcon(heading), zIndexOffset: 2000 });
      layersRef.current.playback.addLayer(vesselMarkerRef.current);
    }

    // Pan camera when playing
    if (externalIsPlaying) {
      mapInstance.current.panTo([lat, lng], { animate: true, duration: 0.1 });
    }

    // Completed / remaining paths
    const completed = coordinates.slice(0, index + 1).map(c => [c[1], c[0]]);
    const remaining = coordinates.slice(index).map(c => [c[1], c[0]]);

    if (completedPathRef.current) layersRef.current.playback.removeLayer(completedPathRef.current);
    if (remainingPathRef.current) layersRef.current.playback.removeLayer(remainingPathRef.current);

    if (completed.length > 1) {
      completedPathRef.current = L.polyline(completed, { color: '#48bb78', weight: 4, opacity: 0.8 });
      layersRef.current.playback.addLayer(completedPathRef.current);
    }

    if (remaining.length > 1) {
      remainingPathRef.current = L.polyline(remaining, { color: '#cbd5e0', weight: 3, opacity: 0.5, dashArray: '10, 5' });
      layersRef.current.playback.addLayer(remainingPathRef.current);
    }

    if (zoneEvents) renderZoneEvents(zoneEvents, index);

    // Update selected datapoint
    const detailedPoint = detailedPointsDataRef.current?.[index]?.properties;
    setSelectedDatapoint({
      index, coordinates: { latitude: lat, longitude: lng },
      timestamp: detailedPoint?.time || null,
      speed: detailedPoint?.sog || null,
      cog: detailedPoint?.cog || null,
      heading: detailedPoint?.hdg || null,
      depth: detailedPoint?.dpt || null,
      routeId: selectedRun.properties?.run_id, routeType: 'run', totalPoints: coordinates.length
    });
  }, [selectedRun, externalIsPlaying, zoneEvents, renderZoneEvents]);

  // React to externalPlaybackIndex changes from the ControlPanel
  useEffect(() => {
    if (isPlaybackActive && selectedRun) {
      updatePlaybackVisuals(externalPlaybackIndex);
    }
  }, [externalPlaybackIndex, isPlaybackActive, selectedRun, updatePlaybackVisuals]);

  const cleanupPlayback = useCallback(() => {
    layersRef.current.playback.clearLayers();
    vesselMarkerRef.current = null;
    completedPathRef.current = null;
    remainingPathRef.current = null;
  }, []);

  const togglePlayback = useCallback(() => {
    const newState = !isPlaybackActive;
    if (!newState) {
      cleanupPlayback();
      if (currentRouteDataRef.current) {
        const { routeData, routeType } = currentRouteDataRef.current;
        renderDatapointMarkers(routeData, routeType);
      }
      if (zoneEvents) renderZoneEvents(zoneEvents);
    } else {
      layersRef.current.datapoints.clearLayers();
      datapointMarkersRef.current.clear();
      if (zoneEvents) renderZoneEvents(zoneEvents, 0);
    }
    setIsPlaybackActive(newState);
    if (onPlaybackActiveChange) onPlaybackActiveChange(newState);
  }, [isPlaybackActive, cleanupPlayback, onPlaybackActiveChange, zoneEvents, renderZoneEvents]);

  const getDrawControlOptions = useCallback((drawType, color) => {
    const shapeOptions = { color, fillColor: color, fillOpacity: 0.2, weight: 3 };
    const drawOptions = { polygon: false, polyline: false, rectangle: false, circle: false, marker: false, circlemarker: false };

    switch (drawType) {
      case 'polygon':
        drawOptions.polygon = {
          allowIntersection: false, showArea: true, maxPoints: 0, shapeOptions,
          drawError: { color: '#e1e100', message: '<strong>Error:</strong> Polygon edges cannot cross!' },
        };
        break;
      case 'polyline':
        drawOptions.polyline = { shapeOptions, metric: true };
        break;
      default:
        drawOptions.polygon = { allowIntersection: false, showArea: true, shapeOptions };
    }
    return drawOptions;
  }, []);

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;
    mapInstance.current = L.map(mapRef.current).setView([45.64, -122.66], 11);

    const getTileLayer = () => {
      switch(mapStyle) {
        case 'minimal': return L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', { attribution: '&copy; OpenStreetMap &copy; CARTO', subdomains: 'abcd', maxZoom: 20 });
        case 'dark': return L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', { attribution: '&copy; OpenStreetMap &copy; CARTO', subdomains: 'abcd', maxZoom: 20 });
        case 'dark-contrast': return L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager_labels_under/{z}/{x}/{y}{r}.png', { attribution: '&copy; OpenStreetMap &copy; CARTO', subdomains: 'abcd', maxZoom: 20 });
        case 'satellite': return L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', { attribution: 'Tiles &copy; Esri' });
        case 'terrain': return L.tileLayer('https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png', { attribution: '&copy; OpenStreetMap, SRTM &copy; OpenTopoMap' });
        default: return L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '&copy; OpenStreetMap contributors' });
      }
    };

    getTileLayer().addTo(mapInstance.current);
    Object.values(layersRef.current).forEach(layer => layer.addTo(mapInstance.current));
    drawnItemsRef.current.addTo(mapInstance.current);

    mapInstance.current.on('zoomend', () => {
      setCurrentZoomLevel(mapInstance.current.getZoom());
    });

    mapInstance.current.on(L.Draw.Event.CREATED, (e) => {
      const layer = e.layer;
      const geojson = layer.toGeoJSON().geometry;
      if (onGeofenceCreatedRef.current) {
        onGeofenceCreatedRef.current(geojson, drawModeRef.current.zoneType);
      }
    });

    // Signal that the map instance is ready for dependent hooks
    setMapReady(true);

    return () => {
      setMapReady(false);
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
    };
  }, [mapStyle]);

  // Drawing mode
  useEffect(() => {
    const map = mapInstance.current;
    if (!map) return;

    if (drawControlRef.current) {
      map.removeControl(drawControlRef.current);
      drawControlRef.current = null;
    }

    if (!drawMode.active) return;

    const zoneColors = {
      segment_zone: '#8b5cf6',
      segment: '#8b5cf6',
      restricted: '#ef4444',
      constrained_corridor: '#3b82f6',
      corridor: '#3b82f6',
    };
    const color = zoneColors[drawMode.zoneType] || '#8b5cf6';

    const controlOptions = getDrawControlOptions('polygon', color);
    const control = new L.Control.Draw(controlOptions);
    map.addControl(control);
    drawControlRef.current = control;

    return () => {
      if (drawControlRef.current && mapInstance.current) {
        mapInstance.current.removeControl(drawControlRef.current);
        drawControlRef.current = null;
      }
    };
  }, [drawMode.active, drawMode.zoneType, getDrawControlOptions]);

  // Zone events rendering
  useEffect(() => {
    if (zoneEvents && !isPlaybackActive) {
      renderZoneEvents(zoneEvents);
    } else if (!zoneEvents) {
      layersRef.current.zoneEvents.clearLayers();
    }
  }, [zoneEvents, isPlaybackActive, renderZoneEvents]);

  // Re-render datapoints on zoom/selection change
  useEffect(() => {
    if (currentRouteDataRef.current && !isPlaybackActive) {
      const { routeData, routeType } = currentRouteDataRef.current;
      renderDatapointMarkers(routeData, routeType);
    }
  }, [currentZoomLevel, selectedDatapoint, isPlaybackActive]);

  // Update map tiles
  useEffect(() => {
    if (!mapInstance.current) return;
    mapInstance.current.eachLayer((layer) => {
      if (layer instanceof L.TileLayer) mapInstance.current.removeLayer(layer);
    });

    const getTileLayer = () => {
      switch(mapStyle) {
        case 'minimal': return L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', { attribution: '&copy; OpenStreetMap &copy; CARTO', subdomains: 'abcd', maxZoom: 20 });
        case 'dark': return L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', { attribution: '&copy; OpenStreetMap &copy; CARTO', subdomains: 'abcd', maxZoom: 20 });
        case 'dark-contrast': return L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager_labels_under/{z}/{x}/{y}{r}.png', { attribution: '&copy; OpenStreetMap &copy; CARTO', subdomains: 'abcd', maxZoom: 20 });
        case 'satellite': return L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', { attribution: 'Tiles &copy; Esri' });
        case 'terrain': return L.tileLayer('https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png', { attribution: '&copy; OpenStreetMap, SRTM &copy; OpenTopoMap' });
        default: return L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '&copy; OpenStreetMap contributors' });
      }
    };
    getTileLayer().addTo(mapInstance.current);
  }, [mapStyle]);

  // Render SOP routes
  useEffect(() => {
    if (!mapInstance.current) return;
    layersRef.current.sopRoutes.clearLayers();

    if (selectedSopRoute) {
      if (!selectedSopRoute.geometry) return;
      try {
        const layer = L.geoJSON(selectedSopRoute, {
          style: { color: '#e603ff', weight: 4, opacity: isPlaybackActive ? 0.4 : 1, dashArray: '10, 5', lineCap: 'round', lineJoin: 'round' },
          onEachFeature: (feature, featureLayer) => {
            const props = feature.properties || {};
            featureLayer.bindPopup(`<div style="font-family: Arial, sans-serif;"><strong style="color: #e603ff;">SOP Route</strong><br><strong>Name:</strong> ${props.route_name || 'Unknown'}<br>${props.distance_nm ? `<strong>Distance:</strong> ${props.distance_nm.toFixed(2)} NM<br>` : ''}${props.waypoint_count ? `<strong>Waypoints:</strong> ${props.waypoint_count}<br>` : ''}</div>`);
          }
        });
        layersRef.current.sopRoutes.addLayer(layer);
        if (!isPlaybackActive) {
          const layerBounds = layer.getBounds();
          if (layerBounds?.isValid()) mapInstance.current.fitBounds(layerBounds, { padding: [50, 50] });
          renderDatapointMarkers(selectedSopRoute, 'sop');
        }
      } catch (err) {
        console.error('[MapComponent] Error rendering selected SOP route:', err);
      }
      return;
    }
    if (!isPlaybackActive) {
      layersRef.current.datapoints.clearLayers();
      datapointMarkersRef.current.clear();
      setSelectedDatapoint(null);
      currentRouteDataRef.current = null;
    }
  }, [sopRoutes, selectedSopRoute, isPlaybackActive]);

  // Render vessel runs
  useEffect(() => {
    if (!mapInstance.current) return;
    layersRef.current.vesselRuns.clearLayers();
    vesselRunLayersRef.current.clear();
    if (!Array.isArray(vesselRuns) || vesselRuns.length === 0) return;

    const hasSelection = !!selectedRun;
    vesselRuns.forEach((run) => {
      if (!run?.geometry) return;
      try {
        const runId = run.properties?.run_id;
        const isSelected = selectedRun?.properties?.run_id === runId;
        if (isPlaybackActive && !isSelected) return;
        const baseOpacity = (!hasSelection || isSelected) ? 1.0 : 0.6;

        const layer = L.geoJSON(run, {
          style: { color: '#ff8103', weight: 2, opacity: isPlaybackActive ? 0 : baseOpacity, lineCap: 'round', lineJoin: 'round' },
          onEachFeature: (feature, featureLayer) => {
            const props = feature.properties || {};
            featureLayer.bindPopup(`<div style="font-family: Arial, sans-serif;"><strong style="color: #ff8103;">Actual Route</strong><br><strong>Vessel:</strong> ${props.vessel_name || 'Unknown'}<br>${props.run_name ? `<strong>Run:</strong> ${props.run_name}<br>` : ''}${props.distance_nm ? `<strong>Distance:</strong> ${props.distance_nm.toFixed(2)} NM<br>` : ''}${props.duration_hours ? `<strong>Duration:</strong> ${props.duration_hours.toFixed(2)} hrs<br>` : ''}${props.avg_speed_knots ? `<strong>Avg Speed:</strong> ${props.avg_speed_knots.toFixed(1)} knots<br>` : ''}</div>`);
            if (!isPlaybackActive) {
              featureLayer.on('mouseover', () => { if (!isSelected) { featureLayer.setStyle({ color: '#ffb366', weight: 3, opacity: 0.9 }); hoveredLayerRef.current = featureLayer; } });
              featureLayer.on('mouseout', () => { if (!isSelected) { featureLayer.setStyle({ color: '#ff8103', weight: 2, opacity: hasSelection ? 0.6 : 1.0 }); hoveredLayerRef.current = null; } });
              featureLayer.on('click', (e) => { featureLayer.openPopup(e.latlng); if (onRunSelect && runId) onRunSelect(runId); setSelectedRouteId(String(runId)); });
            }
          }
        });
        layersRef.current.vesselRuns.addLayer(layer);
        if (runId) vesselRunLayersRef.current.set(runId, layer);
      } catch (err) {
        console.error('[MapComponent] Error rendering vessel run:', err);
      }
    });
  }, [vesselRuns, selectedRun, onRunSelect, isPlaybackActive]);

  // Update vessel run opacity on selection change
  useEffect(() => {
    if (!mapInstance.current || isPlaybackActive) return;
    const selectedRunId = selectedRun?.properties?.run_id;
    const hasSelection = !!selectedRun;
    vesselRunLayersRef.current.forEach((layer, runId) => {
      layer.setStyle({ opacity: (!hasSelection || selectedRunId === runId) ? 1.0 : 0.6 });
    });
  }, [selectedRun, isPlaybackActive]);

  // Highlight selected route
  useEffect(() => {
    if (!mapInstance.current) return;
    layersRef.current.selected.clearLayers();

    if (selectedRun) {
      if (!selectedRun.geometry) return;
      try {
        const layer = L.geoJSON(selectedRun, { style: { color: '#ffa303', weight: 5, opacity: isPlaybackActive ? 0 : 1, lineCap: 'round', lineJoin: 'round', className: 'selected-route-glow' } });
        layersRef.current.selected.addLayer(layer);
        if (!isPlaybackActive) renderDatapointMarkers(selectedRun, 'run');
      } catch (err) { console.error('[MapComponent] Error rendering selected run:', err); }
    } else if (selectedSopRoute) {
      if (!selectedSopRoute.geometry) return;
      try {
        const layer = L.geoJSON(selectedSopRoute, { style: { color: '#e603ff', weight: 5, opacity: isPlaybackActive ? 0 : 1, dashArray: '10, 5', lineCap: 'round', lineJoin: 'round', className: 'selected-route-glow' } });
        layersRef.current.selected.addLayer(layer);
      } catch (err) { console.error('[MapComponent] Error rendering selected SOP route:', err); }
    } else if (!isPlaybackActive) {
      layersRef.current.datapoints.clearLayers();
      datapointMarkersRef.current.clear();
      setSelectedDatapoint(null);
      currentRouteDataRef.current = null;
    }
  }, [selectedRun, selectedSopRoute, isPlaybackActive]);

  useEffect(() => { return () => { cleanupPlayback(); }; }, [cleanupPlayback]);

  // Geofence rendering
  useEffect(() => {
    if (!mapInstance.current || !geofences) return;
    layersRef.current.geofences.clearLayers();

    const getGeofenceStyle = (zoneType) => {
      const styles = {
        restricted: { color: '#ef4444', fillColor: '#ef4444', fillOpacity: 0.2 },
        segment: { color: '#8b5cf6', fillColor: '#8b5cf6', fillOpacity: 0.2 },
        segment_zone: { color: '#8b5cf6', fillColor: '#8b5cf6', fillOpacity: 0.2 },
        corridor: { color: '#3b82f6', fillColor: '#3b82f6', fillOpacity: 0.2 },
        constrained_corridor: { color: '#3b82f6', fillColor: '#3b82f6', fillOpacity: 0.2 },
        start_zone: { color: '#10b981', fillColor: '#10b981', fillOpacity: 0.2 },
        end_zone: { color: '#3b82f6', fillColor: '#3b82f6', fillOpacity: 0.2 },
        waypoint: { color: '#f59e0b', fillColor: '#f59e0b', fillOpacity: 0.2 },
        general: { color: '#6b7280', fillColor: '#6b7280', fillOpacity: 0.2 }
      };
      return styles[zoneType] || styles.general;
    };

    if (geofences.features) {
      geofences.features.forEach(feature => {
        const style = getGeofenceStyle(feature.properties.zone_type);
        const layer = L.geoJSON(feature, {
          style: { ...style, weight: 2, opacity: 0.8, dashArray: '5, 5' },
          onEachFeature: (feature, layer) => {
            const props = feature.properties;
            layer.bindPopup(`<div style="font-family: Arial, sans-serif;"><strong style="color: ${style.color};">${(props.zone_type || '').replace('_', ' ').toUpperCase()}</strong><br><strong>Name:</strong> ${props.name}<br>${props.description ? `<strong>Description:</strong> ${props.description}<br>` : ''}</div>`);
          }
        });
        layersRef.current.geofences.addLayer(layer);
      });
    }
  }, [geofences]);

  return (
    <>
      <div ref={mapRef} className="map-container" />
      <RouteInfoPanel 
        routeId={selectedRouteId}
        selectedDatapoint={selectedDatapoint}
        onClose={() => { setSelectedRouteId(null); setSelectedDatapoint(null); }} 
      />
    </>
  );
};

export default MapComponent;